import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { MapPin, Clock, User, Download, Shield, Smartphone, Navigation, Filter, CheckCircle2, AlertTriangle, Plus, Trash2, WifiOff, RefreshCw, Database, Settings, BarChart3, CircleDot, QrCode, FileDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_COLORS = { check_in: 'text-[#A8E6CF] bg-[#A8E6CF]/10', check_out: 'text-blue-400 bg-blue-500/10' };
const VERIFY_BADGE = {
  gps: { icon: <Navigation className="w-3.5 h-3.5" />, color: 'text-[#00A6A0]', label: 'GPS' },
  nfc: { icon: <Smartphone className="w-3.5 h-3.5" />, color: 'text-[#8B5CF6]', label: 'NFC' },
  manual: { icon: <User className="w-3.5 h-3.5" />, color: 'text-amber-400', label: 'MANUAL' },
};

const OFFLINE_QUEUE_KEY = 'fendanacura_offline_visits';

export default function VisitAuditPage() {
  const { user } = useAuth();
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);
  const isCaregiver = user?.role === 'caregiver';
  const [tab, setTab] = useState(isAdmin ? 'trail' : 'my-history');
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({});
  const [filterDate, setFilterDate] = useState('');
  const [loading, setLoading] = useState(true);
  // NFC state
  const [nfcTags, setNfcTags] = useState([]);
  const [nfcDialog, setNfcDialog] = useState(false);
  const [nfcForm, setNfcForm] = useState({ client_id: '', tag_id: '', location_description: '' });
  const [clients, setClients] = useState([]);
  // Offline sync
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [syncing, setSyncing] = useState(false);
  // Late arrivals / missed visits / grace period
  const [lateArrivals, setLateArrivals] = useState([]);
  const [lateStats, setLateStats] = useState({});
  const [missedVisits, setMissedVisits] = useState([]);
  const [gracePeriod, setGracePeriod] = useState(10);
  const [missedThreshold, setMissedThreshold] = useState(15);
  const [reasonDialog, setReasonDialog] = useState(null);
  const [reasonText, setReasonText] = useState('');
  // Analytics
  const [analytics, setAnalytics] = useState(null);
  // Geofence
  const [geofences, setGeofences] = useState([]);
  const [geoDialog, setGeoDialog] = useState(false);
  const [geoForm, setGeoForm] = useState({ client_id: '', radius_meters: 200, latitude: '', longitude: '' });
  const [geofenceAlerts, setGeofenceAlerts] = useState([]);
  // QR codes
  const [qrDialog, setQrDialog] = useState(null);

  useEffect(() => {
    if (isAdmin) { fetchAudit(); fetchClients(); fetchLateArrivals(); fetchMissedVisits(); fetchGracePeriod(); }
    if (isCaregiver) { fetchMyHistory(); fetchMyLateArrivals(); }
    loadOfflineQueue();
  }, []);

  useEffect(() => {
    if (tab === 'nfc-tags' && isAdmin) fetchNfcTags();
  }, [tab]);

  const fetchAudit = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/visit-audit`;
      if (filterDate) url += `?from=${filterDate}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setEvents(data.events || []);
      setStats(data.stats || {});
    } catch { setEvents([]); }
    setLoading(false);
  };

  const fetchMyHistory = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/visit-audit/my-history`, { withCredentials: true });
      setEvents(data.events || []);
      setStats(data.stats || {});
    } catch { setEvents([]); }
    setLoading(false);
  };

  const fetchNfcTags = async () => {
    try { const { data } = await axios.get(`${API}/api/visit-audit/nfc/tags`, { withCredentials: true }); setNfcTags(data); } catch { setNfcTags([]); }
  };

  const fetchClients = async () => {
    try { const { data } = await axios.get(`${API}/api/clinical/my-clients`, { withCredentials: true }); setClients(data); } catch { setClients([]); }
  };

  const fetchLateArrivals = async () => {
    try {
      const { data } = await axios.get(`${API}/api/visit-audit/late-arrivals`, { withCredentials: true });
      setLateArrivals(data.late_arrivals || []);
      setLateStats(data.stats || {});
    } catch { /* ignore */ }
  };

  const fetchMissedVisits = async () => {
    try { const { data } = await axios.get(`${API}/api/visit-audit/missed-visits`, { withCredentials: true }); setMissedVisits(data); } catch { setMissedVisits([]); }
  };

  const fetchGracePeriod = async () => {
    try {
      const { data } = await axios.get(`${API}/api/org/grace-period`, { withCredentials: true });
      setGracePeriod(data.grace_period_minutes);
      setMissedThreshold(data.missed_visit_threshold_minutes);
    } catch { /* ignore */ }
  };

  const updateGracePeriod = async () => {
    try {
      await axios.put(`${API}/api/org/grace-period`, { grace_period_minutes: gracePeriod, missed_visit_threshold_minutes: missedThreshold }, { withCredentials: true });
      toast.success('Grace period updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const submitLateReason = async () => {
    if (!reasonDialog || !reasonText) return;
    try {
      await axios.put(`${API}/api/visit-audit/late-arrivals/${reasonDialog}/reason`, { reason: reasonText }, { withCredentials: true });
      toast.success('Reason recorded');
      setReasonDialog(null);
      setReasonText('');
      fetchLateArrivals();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const fetchAnalytics = async () => {
    try { const { data } = await axios.get(`${API}/api/visit-audit/analytics/late-arrivals`, { withCredentials: true }); setAnalytics(data); } catch { /* ignore */ }
  };

  const fetchGeofences = async () => {
    try { const { data } = await axios.get(`${API}/api/visit-audit/geofences`, { withCredentials: true }); setGeofences(data); } catch { setGeofences([]); }
  };

  const fetchGeofenceAlerts = async () => {
    try { const { data } = await axios.get(`${API}/api/visit-audit/geofence-alerts`, { withCredentials: true }); setGeofenceAlerts(data); } catch { setGeofenceAlerts([]); }
  };

  const [myLateArrivals, setMyLateArrivals] = useState([]);
  const [myPendingCount, setMyPendingCount] = useState(0);
  const fetchMyLateArrivals = async () => {
    try {
      const { data } = await axios.get(`${API}/api/visit-audit/my-late-arrivals`, { withCredentials: true });
      setMyLateArrivals(data.late_arrivals || []);
      setMyPendingCount(data.pending_count || 0);
    } catch { /* ignore */ }
  };

  const submitMyLateReason = async (shiftId) => {
    if (!reasonText) return;
    try {
      await axios.put(`${API}/api/visit-audit/my-late-arrivals/${shiftId}/reason`, { reason: reasonText }, { withCredentials: true });
      toast.success('Reason submitted');
      setReasonDialog(null);
      setReasonText('');
      fetchMyLateArrivals();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const fetchQrCode = async (tagDbId) => {
    try {
      const { data } = await axios.get(`${API}/api/visit-audit/nfc/tags/${tagDbId}/qr`, { withCredentials: true });
      setQrDialog(data);
    } catch { toast.error('QR generation failed'); }
  };

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API}/api/visit-audit/export/csv`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `visit_audit_trail_${new Date().toISOString().split('T')[0]}.csv`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch { toast.error('Export failed'); }
  };

  const seedDemo = async () => {
    try {
      const { data } = await axios.post(`${API}/api/visit-audit/seed-demo`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchAudit();
    } catch (err) { toast.error(err.response?.data?.detail || 'Seed failed'); }
  };

  const registerNfc = async () => {
    try {
      await axios.post(`${API}/api/visit-audit/nfc/register`, nfcForm, { withCredentials: true });
      toast.success('NFC tag registered');
      setNfcDialog(false);
      setNfcForm({ client_id: '', tag_id: '', location_description: '' });
      fetchNfcTags();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deactivateNfc = async (id) => {
    if (!window.confirm('Deactivate this NFC tag?')) return;
    try {
      await axios.put(`${API}/api/visit-audit/nfc/tags/${id}/deactivate`, {}, { withCredentials: true });
      toast.success('NFC tag deactivated');
      fetchNfcTags();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Offline sync
  const loadOfflineQueue = () => {
    try {
      const q = JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]');
      setOfflineQueue(q);
    } catch { setOfflineQueue([]); }
  };

  const syncOfflineEvents = async () => {
    if (offlineQueue.length === 0) { toast.info('No offline events to sync'); return; }
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/api/visit-audit/offline-sync`, { events: offlineQueue }, { withCredentials: true });
      toast.success(data.message);
      localStorage.removeItem(OFFLINE_QUEUE_KEY);
      setOfflineQueue([]);
      if (isAdmin) fetchAudit();
      if (isCaregiver) fetchMyHistory();
    } catch (err) { toast.error('Sync failed - will retry when online'); }
    setSyncing(false);
  };

  const renderEvent = (ev) => {
    const vb = VERIFY_BADGE[ev.verification_method] || VERIFY_BADGE.manual;
    return (
      <Card key={ev.id} className="glass-card border-border" data-testid={`visit-event-${ev.id}`}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`px-2 py-1 rounded-md text-[10px] font-medium whitespace-nowrap ${EVENT_COLORS[ev.event_type] || 'bg-muted text-muted-foreground'}`}>
                {ev.event_type === 'check_in' ? 'CLOCK IN' : 'CLOCK OUT'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground font-medium">{ev.caregiver_name}</span>
                  <span className="text-[10px] text-muted-foreground">for</span>
                  <span className="text-sm text-[#00A6A0] font-medium">{ev.client_name}</span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />{new Date(ev.timestamp).toLocaleString()}
                  </span>
                  <span className="text-[10px] text-muted-foreground">{ev.shift_date} {ev.shift_time}</span>
                  {ev.distance_from_client_meters != null && (
                    <span className={`text-[10px] flex items-center gap-0.5 ${ev.distance_from_client_meters <= 200 ? 'text-[#A8E6CF]' : 'text-amber-400'}`}>
                      {ev.distance_from_client_meters <= 200 ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                      {Math.round(ev.distance_from_client_meters)}m
                    </span>
                  )}
                  {ev.synced_from_offline && (
                    <span className="text-[10px] text-amber-400 flex items-center gap-0.5"><WifiOff className="w-3 h-3" />Offline sync</span>
                  )}
                </div>
              </div>
            </div>
            <div className={`flex items-center gap-1.5 flex-shrink-0 ${vb.color}`}>
              {vb.icon}
              <span className="text-[10px] uppercase">{vb.label}</span>
            </div>
          </div>
          {ev.gps_location && (
            <p className="text-[10px] text-muted-foreground mt-1 font-mono">
              GPS: {ev.gps_location.latitude?.toFixed(6)}, {ev.gps_location.longitude?.toFixed(6)}
              {ev.gps_location.accuracy ? ` (acc: ${Math.round(ev.gps_location.accuracy)}m)` : ''}
            </p>
          )}
          {ev.nfc_tag_id && <p className="text-[10px] text-[#8B5CF6] mt-0.5">NFC Tag: {ev.nfc_tag_id}</p>}
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="visit-audit-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
              <MapPin className="w-6 h-6 text-[#00A6A0]" />
              {isCaregiver ? 'My Visit History' : 'Visit Audit Trail'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isCaregiver ? 'Your GPS clock-in/out history with verification proof' : 'GPS clock-in/out and NFC verification log - CQC inspection ready'}
            </p>
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {isAdmin && (
              <>
                <Button size="sm" variant="outline" className="border-border text-xs" onClick={seedDemo} data-testid="seed-demo-btn">
                  <Database className="w-4 h-4 mr-1" />Seed Demo Data
                </Button>
                <Button size="sm" variant="outline" className="border-border text-xs" onClick={exportCSV} data-testid="export-csv-btn">
                  <Download className="w-4 h-4 mr-1" />Export CSV
                </Button>
              </>
            )}
            {offlineQueue.length > 0 && (
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white text-xs" onClick={syncOfflineEvents} disabled={syncing} data-testid="sync-offline-btn">
                <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />{syncing ? 'Syncing...' : `Sync ${offlineQueue.length} Offline`}
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="glass-card border-border" data-testid="stat-total">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-foreground">{stats.total_events || 0}</p>
              <p className="text-xs text-muted-foreground">Total Events</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-[#00A6A0]/30" data-testid="stat-gps">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Navigation className="w-4 h-4 text-[#00A6A0]" />
                <p className="text-2xl font-bold text-[#00A6A0]">{stats.gps_verified || 0}</p>
              </div>
              <p className="text-xs text-muted-foreground">GPS Verified</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-[#8B5CF6]/30" data-testid="stat-nfc">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Smartphone className="w-4 h-4 text-[#8B5CF6]" />
                <p className="text-2xl font-bold text-[#8B5CF6]">{stats.nfc_verified || 0}</p>
              </div>
              <p className="text-xs text-muted-foreground">NFC Verified</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-amber-500/30" data-testid="stat-manual">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <User className="w-4 h-4 text-amber-400" />
                <p className="text-2xl font-bold text-amber-400">{stats.manual_entries || 0}</p>
              </div>
              <p className="text-xs text-muted-foreground">Manual Entries</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs (Admin only) */}
        {isAdmin && (
          <div className="flex gap-1 p-1 bg-muted rounded-lg overflow-x-auto scrollbar-hide">
            <button onClick={() => { setTab('trail'); fetchAudit(); }} className={`py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${tab === 'trail' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-trail">
              Audit Trail
            </button>
            <button onClick={() => setTab('nfc-tags')} className={`py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${tab === 'nfc-tags' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-nfc">
              NFC Tags
            </button>
            <button onClick={() => { setTab('late'); fetchLateArrivals(); }} className={`py-2 px-3 rounded-md text-sm font-medium transition-colors relative whitespace-nowrap flex-shrink-0 ${tab === 'late' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-late">
              Late Arrivals
              {lateStats.pending_reason > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center">{lateStats.pending_reason}</span>}
            </button>
            <button onClick={() => { setTab('missed'); fetchMissedVisits(); }} className={`py-2 px-3 rounded-md text-sm font-medium transition-colors relative whitespace-nowrap flex-shrink-0 ${tab === 'missed' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-missed">
              Missed Visits
              {missedVisits.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{missedVisits.length}</span>}
            </button>
            <button onClick={() => setTab('settings')} className={`py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${tab === 'settings' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-settings">
              Settings
            </button>
            <button onClick={() => { setTab('analytics'); fetchAnalytics(); }} className={`py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${tab === 'analytics' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-analytics">
              Analytics
            </button>
            <button onClick={() => { setTab('geofence'); fetchGeofences(); fetchGeofenceAlerts(); }} className={`py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${tab === 'geofence' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-geofence">
              Geofence
            </button>
          </div>
        )}

        {/* Offline Sync Banner (Caregiver) */}
        {isCaregiver && offlineQueue.length > 0 && (
          <Card className="glass-card border-amber-500/40" data-testid="offline-banner">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <WifiOff className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-foreground font-medium">{offlineQueue.length} visit event{offlineQueue.length > 1 ? 's' : ''} waiting to sync</span>
                </div>
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white text-xs" onClick={syncOfflineEvents} disabled={syncing}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />Sync Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ====== AUDIT TRAIL TAB ====== */}
        {(tab === 'trail' || tab === 'my-history') && (
          <>
            {isAdmin && (
              <div className="flex gap-2 flex-wrap items-end">
                <div>
                  <label className="text-xs text-muted-foreground">Date From</label>
                  <Input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} className="bg-muted border-border text-foreground w-40 mt-1" data-testid="filter-date" />
                </div>
                <Button size="sm" className="btn-primary" onClick={fetchAudit} data-testid="apply-filters-btn">
                  <Filter className="w-4 h-4 mr-1" />Filter
                </Button>
              </div>
            )}

            <Card className="glass-card border-[#00A6A0]/30">
              <CardContent className="p-3">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-[#00A6A0] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-foreground">CQC Inspection Ready</p>
                    <p className="text-[10px] text-muted-foreground">Every visit is immutably logged with GPS coordinates, accuracy, distance from client, NFC tag verification, caregiver identity, and timestamp. {isAdmin ? 'Exportable as CSV for CQC evidence packs.' : 'Your visits are tracked for care quality assurance.'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="text-center py-8"><p className="text-muted-foreground">Loading audit trail...</p></div>
            ) : events.length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" />
                <p className="text-muted-foreground">No visit events recorded yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {isAdmin ? 'Click "Seed Demo Data" to populate with realistic sample data, or events log automatically when caregivers clock in/out.' : 'Events are logged when you clock in/out of shifts.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="events-list">
                {events.map(ev => renderEvent(ev))}
              </div>
            )}
          </>
        )}

        {/* ====== NFC TAG MANAGEMENT TAB ====== */}
        {tab === 'nfc-tags' && isAdmin && (
          <div className="space-y-4" data-testid="nfc-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Register NFC tags at client locations for verified check-in</p>
              <Button size="sm" className="btn-primary" onClick={() => setNfcDialog(true)} data-testid="register-nfc-btn">
                <Plus className="w-4 h-4 mr-1" />Register NFC Tag
              </Button>
            </div>

            <Card className="glass-card border-[#8B5CF6]/20">
              <CardContent className="p-3">
                <div className="flex items-start gap-2">
                  <Smartphone className="w-4 h-4 text-[#8B5CF6] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-foreground">How NFC Tags Work</p>
                    <p className="text-[10px] text-muted-foreground">Place an NFC tag sticker at each client location (e.g., front door). When caregivers tap their phone on the tag before starting care, the system verifies they are physically present. This provides tamper-proof location verification for CQC.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {nfcTags.length === 0 ? (
              <div className="text-center py-8">
                <Smartphone className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" />
                <p className="text-muted-foreground">No NFC tags registered</p>
                <p className="text-xs text-muted-foreground mt-1">Register tags to enable tap-to-verify at client locations</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {nfcTags.map(tag => (
                  <Card key={tag.id} className="glass-card border-border" data-testid={`nfc-tag-${tag.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Smartphone className="w-4 h-4 text-[#8B5CF6]" />
                            <span className="text-sm text-foreground font-medium font-mono">{tag.tag_id}</span>
                          </div>
                          <p className="text-xs text-[#00A6A0] mt-1">{tag.client_name}</p>
                          <p className="text-[10px] text-muted-foreground">{tag.location_description} - Registered {new Date(tag.registered_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="text-[#00A6A0] hover:text-[#00A6A0]/80 h-8 w-8 p-0" onClick={() => fetchQrCode(tag.id)} data-testid={`qr-nfc-${tag.id}`} title="Generate QR Code">
                            <QrCode className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-500 h-8 w-8 p-0" onClick={() => deactivateNfc(tag.id)} data-testid={`deactivate-nfc-${tag.id}`} title="Deactivate tag">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== LATE ARRIVALS TAB ====== */}
        {tab === 'late' && isAdmin && (
          <div className="space-y-4" data-testid="late-arrivals-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{lateStats.total_late || 0} late arrivals, {lateStats.pending_reason || 0} awaiting reason</p>
            </div>
            {lateArrivals.length === 0 ? (
              <div className="text-center py-8"><CheckCircle2 className="w-12 h-12 text-[#A8E6CF] mx-auto mb-3 opacity-50" /><p className="text-muted-foreground">No late arrivals recorded</p></div>
            ) : (
              <div className="space-y-2">
                {lateArrivals.map(la => (
                  <Card key={la.id} className="glass-card border-border" data-testid={`late-${la.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm text-foreground font-medium">{la.caregiver_name}</span>
                            <span className="text-[10px] text-muted-foreground">for</span>
                            <span className="text-sm text-[#00A6A0]">{la.client_name}</span>
                            <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-[10px] font-medium">{la.minutes_late} min late</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{la.date} - Scheduled: {la.start_time} - Arrived: {la.check_in ? new Date(la.check_in).toLocaleTimeString() : 'N/A'}</p>
                          {la.late_reason === 'pending' ? (
                            <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-400 text-xs mt-1" onClick={() => { setReasonDialog(la.id); setReasonText(''); }} data-testid={`add-reason-${la.id}`}>Add Reason</Button>
                          ) : (
                            <p className="text-[10px] text-muted-foreground mt-1">Reason: {la.late_reason}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== MISSED VISITS TAB ====== */}
        {tab === 'missed' && isAdmin && (
          <div className="space-y-4" data-testid="missed-visits-section">
            <p className="text-sm text-muted-foreground">{missedVisits.length} missed visit alerts</p>
            {missedVisits.length === 0 ? (
              <div className="text-center py-8"><CheckCircle2 className="w-12 h-12 text-[#A8E6CF] mx-auto mb-3 opacity-50" /><p className="text-muted-foreground">No missed visits</p></div>
            ) : (
              <div className="space-y-2">
                {missedVisits.map(mv => (
                  <Card key={mv.id} className="glass-card border-red-500/30" data-testid={`missed-${mv.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                        <span className="text-sm text-foreground font-medium">{mv.caregiver_name}</span>
                        <span className="text-[10px] text-muted-foreground">for</span>
                        <span className="text-sm text-[#00A6A0]">{mv.client_name}</span>
                        <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 text-[10px] font-medium">{mv.minutes_overdue} min overdue</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">{mv.shift_date} - Scheduled: {mv.scheduled_time} - Threshold: {mv.threshold_minutes}min</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== SETTINGS TAB ====== */}
        {tab === 'settings' && isAdmin && (
          <div className="space-y-4" data-testid="visit-settings-section">
            <Card className="glass-card border-border">
              <CardContent className="p-4 space-y-4">
                <p className="text-sm font-medium text-foreground">Grace Period & Missed Visit Thresholds</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground">Late Arrival Grace Period (minutes)</label>
                    <p className="text-[10px] text-muted-foreground mb-1">Caregivers arriving within this window are not flagged</p>
                    <Input type="number" min="5" max="60" value={gracePeriod} onChange={e => setGracePeriod(parseInt(e.target.value) || 5)}
                      className="bg-muted border-border text-foreground w-24" data-testid="grace-period-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Missed Visit Threshold (minutes)</label>
                    <p className="text-[10px] text-muted-foreground mb-1">Alert admins if no check-in after this many minutes</p>
                    <Input type="number" min="5" max="120" value={missedThreshold} onChange={e => setMissedThreshold(parseInt(e.target.value) || 15)}
                      className="bg-muted border-border text-foreground w-24" data-testid="missed-threshold-input" />
                  </div>
                </div>
                <Button className="btn-primary" onClick={updateGracePeriod} data-testid="save-grace-btn">Save Settings</Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ====== ANALYTICS TAB ====== */}
        {tab === 'analytics' && isAdmin && analytics && (
          <div className="space-y-4" data-testid="analytics-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{analytics.total_all_time} total late arrivals all time</p>
              <Button size="sm" variant="outline" className="border-border text-xs" data-testid="download-analytics-pdf"
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/api/visit-audit/analytics/late-arrivals/pdf`, { withCredentials: true, responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    const a = document.createElement('a'); a.href = url;
                    a.download = `late_arrival_analytics_${new Date().toISOString().split('T')[0]}.pdf`;
                    a.click(); window.URL.revokeObjectURL(url);
                    toast.success('PDF downloaded');
                  } catch { toast.error('PDF export failed'); }
                }}>
                <FileDown className="w-4 h-4 mr-1" />Download PDF
              </Button>
            </div>

            {/* Monthly Trend Chart */}
            <Card className="glass-card border-border">
              <CardContent className="p-4">
                <p className="text-sm font-medium text-foreground mb-3">Monthly Late Arrivals (Last 6 Months)</p>
                <div className="h-48 min-h-[192px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.months}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month_label" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                      <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--foreground))' }} />
                      <Bar dataKey="total_late" name="Late Arrivals" radius={[4, 4, 0, 0]}>
                        {analytics.months.map((m, i) => <Cell key={i} fill={m.total_late > 5 ? '#ef4444' : m.total_late > 0 ? '#f59e0b' : '#A8E6CF'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Top Caregivers */}
            {analytics.top_caregivers?.length > 0 && (
              <Card className="glass-card border-border">
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-foreground mb-3">Caregiver Late Arrival Summary (All Time)</p>
                  <div className="space-y-2">
                    {analytics.top_caregivers.map((cg, i) => (
                      <div key={cg.caregiver_id} className="flex items-center justify-between bg-muted/50 rounded-lg p-2" data-testid={`analytics-cg-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-amber-500/10 text-amber-400 text-xs flex items-center justify-center font-bold">{i + 1}</span>
                          <span className="text-sm text-foreground">{cg.caregiver_name}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-amber-400 font-medium">{cg.late_count} late</span>
                          <span className="text-muted-foreground">avg {cg.avg_minutes_late} min</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ====== GEOFENCE TAB ====== */}
        {tab === 'geofence' && isAdmin && (
          <div className="space-y-4" data-testid="geofence-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Set a radius around each client location for check-in verification</p>
              <Button size="sm" className="btn-primary" onClick={() => setGeoDialog(true)} data-testid="add-geofence-btn">
                <CircleDot className="w-4 h-4 mr-1" />Set Geofence
              </Button>
            </div>

            {/* Existing geofences */}
            {geofences.length === 0 ? (
              <div className="text-center py-8"><CircleDot className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No geofences configured</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {geofences.map(g => {
                  const client = clients.find(c => (c.id || c._id) === g.client_id);
                  return (
                    <Card key={g.id} className="glass-card border-border" data-testid={`geofence-${g.id}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-foreground font-medium">{client?.name || g.client_id}</p>
                            <p className="text-[10px] text-muted-foreground">Radius: {g.radius_meters}m - {g.latitude?.toFixed(4)}, {g.longitude?.toFixed(4)}</p>
                          </div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${g.enabled ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-muted text-muted-foreground'}`}>
                            {g.enabled ? 'Active' : 'Disabled'}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Recent geofence alerts */}
            {geofenceAlerts.length > 0 && (
              <Card className="glass-card border-red-500/30">
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-foreground mb-2">Recent Geofence Breaches</p>
                  <div className="space-y-2">
                    {geofenceAlerts.slice(0, 10).map(a => (
                      <div key={a.id} className="flex items-center gap-2 text-xs bg-red-500/5 rounded-lg p-2" data-testid={`geo-alert-${a.id}`}>
                        <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0" />
                        <span className="text-foreground font-medium">{a.caregiver_name}</span>
                        <span className="text-muted-foreground">checked in {Math.round(a.distance_meters)}m from {a.client_name} (allowed: {a.radius_meters}m)</span>
                        <span className="text-muted-foreground ml-auto">{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ====== CAREGIVER LATE REASONS (Caregiver view) ====== */}
        {isCaregiver && myPendingCount > 0 && (
          <Card className="glass-card border-amber-500/40" data-testid="caregiver-late-banner">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-medium text-foreground">{myPendingCount} late arrival{myPendingCount > 1 ? 's' : ''} need{myPendingCount === 1 ? 's' : ''} a reason</span>
              </div>
              <div className="space-y-2">
                {myLateArrivals.filter(la => la.late_reason === 'pending').map(la => (
                  <div key={la.id} className="flex items-center justify-between bg-muted/50 rounded-lg p-2" data-testid={`my-late-${la.id}`}>
                    <div>
                      <span className="text-xs text-foreground">{la.date} - {la.client_name} - {la.minutes_late} min late</span>
                    </div>
                    <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-400 text-xs" onClick={() => { setReasonDialog(la.id); setReasonText(''); }} data-testid={`my-reason-${la.id}`}>Add Reason</Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Late Arrival Reason Dialog */}
      <Dialog open={!!reasonDialog} onOpenChange={() => setReasonDialog(null)}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle className="text-foreground">Late Arrival Reason</DialogTitle><DialogDescription>Provide the reason for this late arrival.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <textarea value={reasonText} onChange={e => setReasonText(e.target.value)} placeholder="e.g., Traffic congestion, previous visit overran, public transport delay..."
              className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[80px]" data-testid="reason-input" />
            <Button className="w-full btn-primary" onClick={() => isCaregiver ? submitMyLateReason(reasonDialog) : submitLateReason()} disabled={!reasonText.trim()} data-testid="submit-reason-btn">Submit Reason</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={!!qrDialog} onOpenChange={() => setQrDialog(null)}>
        <DialogContent className="bg-background border-border max-w-sm">
          <DialogHeader><DialogTitle className="text-foreground">NFC Tag QR Code</DialogTitle><DialogDescription>Print this QR code as fallback for devices without NFC.</DialogDescription></DialogHeader>
          {qrDialog && (
            <div className="text-center space-y-3">
              <img src={qrDialog.qr_code} alt="QR Code" className="mx-auto w-48 h-48 rounded-lg" data-testid="qr-code-image" />
              <div>
                <p className="text-sm text-foreground font-medium">{qrDialog.tag_id}</p>
                <p className="text-xs text-[#00A6A0]">{qrDialog.client_name}</p>
                <p className="text-[10px] text-muted-foreground">{qrDialog.location}</p>
              </div>
              <Button size="sm" variant="outline" className="border-border" onClick={() => {
                const w = window.open();
                w.document.write(`<html><head><title>NFC Tag: ${qrDialog.tag_id}</title></head><body style="text-align:center;font-family:system-ui;padding:40px;"><h2>${qrDialog.tag_id}</h2><p>${qrDialog.client_name} - ${qrDialog.location}</p><img src="${qrDialog.qr_code}" style="width:300px;height:300px;" /><p style="font-size:10px;color:#888;">Scan this QR code with the FendanaCura app to check in</p></body></html>`);
                w.document.close();
                w.print();
              }} data-testid="print-qr-btn">Print QR Code</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Geofence Dialog */}
      <Dialog open={geoDialog} onOpenChange={setGeoDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle className="text-foreground">Set Client Geofence</DialogTitle><DialogDescription>Configure the allowed check-in radius around a client location.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Client</label>
              <select value={geoForm.client_id} onChange={e => setGeoForm(f => ({ ...f, client_id: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1" data-testid="geo-client-select">
                <option value="">Select client...</option>
                {clients.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground">Latitude</label>
                <Input type="number" step="0.0001" value={geoForm.latitude} onChange={e => setGeoForm(f => ({ ...f, latitude: e.target.value }))} placeholder="51.5074" className="bg-muted border-border text-foreground mt-1" data-testid="geo-lat" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Longitude</label>
                <Input type="number" step="0.0001" value={geoForm.longitude} onChange={e => setGeoForm(f => ({ ...f, longitude: e.target.value }))} placeholder="-0.1278" className="bg-muted border-border text-foreground mt-1" data-testid="geo-lon" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Radius (meters): {geoForm.radius_meters}m</label>
              <input type="range" min="50" max="2000" step="50" value={geoForm.radius_meters} onChange={e => setGeoForm(f => ({ ...f, radius_meters: parseInt(e.target.value) }))} className="w-full mt-1" data-testid="geo-radius" />
              <div className="flex justify-between text-[10px] text-muted-foreground"><span>50m</span><span>2000m</span></div>
            </div>
            <Button className="w-full btn-primary" data-testid="save-geofence-btn" disabled={!geoForm.client_id || !geoForm.latitude || !geoForm.longitude}
              onClick={async () => {
                try {
                  await axios.put(`${API}/api/visit-audit/geofence/${geoForm.client_id}`, { ...geoForm, latitude: parseFloat(geoForm.latitude), longitude: parseFloat(geoForm.longitude), enabled: true }, { withCredentials: true });
                  toast.success('Geofence set');
                  setGeoDialog(false);
                  fetchGeofences();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              }}>
              Save Geofence
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* NFC Registration Dialog */}
      <Dialog open={nfcDialog} onOpenChange={setNfcDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Register NFC Tag</DialogTitle>
            <DialogDescription>Link an NFC tag to a client location for verified check-in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Client</label>
              <select value={nfcForm.client_id} onChange={e => setNfcForm(f => ({ ...f, client_id: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1" data-testid="nfc-client-select">
                <option value="">Select client...</option>
                {clients.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
              </select>
            </div>
            <Input value={nfcForm.tag_id} onChange={e => setNfcForm(f => ({ ...f, tag_id: e.target.value }))} placeholder="NFC Tag ID (e.g., NFC-001)" className="bg-muted border-border text-foreground" data-testid="nfc-tag-id-input" />
            <Input value={nfcForm.location_description} onChange={e => setNfcForm(f => ({ ...f, location_description: e.target.value }))} placeholder="Location (e.g., Front door, Kitchen)" className="bg-muted border-border text-foreground" data-testid="nfc-location-input" />
            <Button className="w-full btn-primary" onClick={registerNfc} disabled={!nfcForm.client_id || !nfcForm.tag_id} data-testid="submit-nfc-btn">
              Register NFC Tag
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
